/**
 * Static option lists + types for the inline VisualDx diagnostic flow.
 *
 * Shape mirrors the canonical Loom demo's 4-step input flow:
 *   1. region        — Front/Back toggle + 12-region grid (multi-select)
 *   2. morphology    — wrap of 8 finding pills (multi-select)
 *   3. age           — 5 age-band chips OR exact-age numeric input
 *   4. onset         — 5 onset-speed cards with line-graph icons (single-select)
 *
 * Lifted out of the parent component so the flow file stays under the
 * 300-line cap. The label strings are the source of truth for the
 * natural-language summary the LLM receives on submission, so keep them
 * clinician-readable rather than ID-shaped.
 */

export type Step = 'region' | 'age' | 'onset' | 'morphology';

/**
 * Step order matches the Loom demo:
 *   region (Where is it located?) -> age (What's the patient's age?) ->
 *   onset (How did the condition start?) -> morphology (Which findings
 *   are present? -> "Build differential" -> ranked differential page).
 *
 * Morphology is intentionally last because the demo's terminal CTA
 * ("Build differential") only appears on that screen — clicking it
 * submits the whole flow rather than advancing to a "review" step.
 * There is no review screen in the demo; submission goes straight to
 * the ranked-differential output widget.
 */
export const STEP_ORDER: Step[] = ['region', 'age', 'onset', 'morphology'];

/** Label shown on the terminal-step (morphology) submit button. */
export const TERMINAL_STEP_CTA_LABEL = 'Build differential';

// ── Region (step 1) ─────────────────────────────────────────────────

export type BodyAspect = 'front' | 'back';

export type Region =
  | 'face'
  | 'scalp'
  | 'neck'
  | 'chest'
  | 'trunk'
  | 'arm'
  | 'forearm'
  | 'hand'
  | 'leg'
  | 'foot'
  | 'genitals'
  | 'other';

export interface RegionOption {
  id: Region;
  label: string;
}

/**
 * Twelve regions in row-major order, matching the demo's 4x3 grid:
 *   row 1: Face / Scalp / Neck
 *   row 2: Chest / Trunk / Arm
 *   row 3: Forearm / Hand / Leg
 *   row 4: Foot / Genitals / Other
 */
export const REGION_OPTIONS: RegionOption[] = [
  { id: 'face', label: 'Face' },
  { id: 'scalp', label: 'Scalp' },
  { id: 'neck', label: 'Neck' },
  { id: 'chest', label: 'Chest' },
  { id: 'trunk', label: 'Trunk' },
  { id: 'arm', label: 'Arm' },
  { id: 'forearm', label: 'Forearm' },
  { id: 'hand', label: 'Hand' },
  { id: 'leg', label: 'Leg' },
  { id: 'foot', label: 'Foot' },
  { id: 'genitals', label: 'Genitals' },
  { id: 'other', label: 'Other' },
];

// ── Morphology (step 2) ─────────────────────────────────────────────

export type Morphology =
  | 'widespreadItching'
  | 'urticarialWheals'
  | 'erythematousMacules'
  | 'scalyPlaques'
  | 'vesiclesBullae'
  | 'nocturnalItchWorsens'
  | 'newMedicationLast2Weeks'
  | 'palpablePurpura';

export interface MorphologyOption {
  id: Morphology;
  label: string;
}

export const MORPHOLOGY_OPTIONS: MorphologyOption[] = [
  { id: 'widespreadItching', label: 'Widespread itching' },
  { id: 'urticarialWheals', label: 'Urticarial wheals' },
  { id: 'erythematousMacules', label: 'Erythematous macules' },
  { id: 'scalyPlaques', label: 'Scaly plaques' },
  { id: 'vesiclesBullae', label: 'Vesicles / bullae' },
  { id: 'nocturnalItchWorsens', label: 'Nocturnal itch worsens' },
  { id: 'newMedicationLast2Weeks', label: 'New medication in last 2 weeks' },
  { id: 'palpablePurpura', label: 'Palpable purpura' },
];

// ── Age (step 3) ────────────────────────────────────────────────────

export type AgeBand = 'infant' | 'child' | 'adolescent' | 'adult' | 'olderAdult';

export interface AgeBandOption {
  id: AgeBand;
  label: string;
  range: string;
}

export const AGE_BAND_OPTIONS: AgeBandOption[] = [
  { id: 'infant', label: 'Infant', range: '< 2 y' },
  { id: 'child', label: 'Child', range: '2-12 y' },
  { id: 'adolescent', label: 'Adolescent', range: '13-17 y' },
  { id: 'adult', label: 'Adult', range: '18-64 y' },
  { id: 'olderAdult', label: 'Older adult', range: '65+ y' },
];

// ── Onset (step 4) ──────────────────────────────────────────────────

export type OnsetSpeed =
  | 'rapidly'
  | 'acutely'
  | 'chronically'
  | 'recurring'
  | 'presentAtBirth';

export interface OnsetOption {
  id: OnsetSpeed;
  label: string;
  hint: string;
}

export const ONSET_OPTIONS: OnsetOption[] = [
  { id: 'rapidly', label: 'Rapidly', hint: 'Hours - minutes to <24 h' },
  { id: 'acutely', label: 'Acutely', hint: 'Days to a few weeks' },
  { id: 'chronically', label: 'Chronically', hint: 'Weeks to months, gradual' },
  { id: 'recurring', label: 'Recurring', hint: 'Comes and goes over time' },
  { id: 'presentAtBirth', label: 'Present at birth', hint: 'Congenital / lifelong' },
];
